import { WTPayload, WTValue } from '@js/WTPayload'

const isAnObject = (variable: any): boolean => {
  return (
    typeof variable === 'object' &&
    !Array.isArray(variable) &&
    variable !== null
  )
}

//If the value in the request payload is a list it needs to be converted to a single comma separated string, e.g.
// [“kfw_40”, “kfw_40_plus”] => “kfw_40,kfw_40_plus”
// it doesn't cover the case when array contains objects
export const joinArrayOfPrimitivesToString = (payload: object) => {
  return Object.entries(payload)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.join(',')]
      }
      return [key, value]
    })
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
}

// If the value in the request payload is an object, return only bottom-level values, but keep the path to the value, e.g
// { opportunity: { nid: 'xxx' } } => { `opportunity.nid`: 'xxx' }
export const flattenThePayload = (
  payload: any,
  keyPrefix: string = ''
): object => {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    const propertyName = `${keyPrefix}${key}`
    if (!isAnObject(value)) {
      return { ...acc, [`${keyPrefix}${key}`]: value }
    }
    return {
      ...acc,
      ...flattenThePayload(value, `${propertyName}.`)
    }
  }, {})
}

const trackingAllowedFields = [
  'gender',
  'country_prefix',
  'postal_code',
  'city',
  'country',
  'position',
  'agreed_to_sales_agent_app_terms',
  'active',
  'delete_reason',
  'delivery_region_postal_code',
  'delivery_region_country',
  'contact_preference',
  'property',
  'construction_start',
  'expansion_stages',
  'utilizations',
  'energy_standards',
  'construction_methods',
  'budget',
  'living_area_total',
  'number_of_rooms',
  'financing_status',
  'country_prefix',
  'detail',
  'type',
  'due_date',
  'time_start',
  'time_end',
  'opportunity',
  'nid',
  'sales_agent_app_daily_task_summary',
  'sales_agent_app_weekly_task_summary',
  'houses_requested',
  'priority'
]

const hideDisallowedToTrackData = (
  payload: object,
  explicitlyAllowedFields?: string[]
) => {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    const keySplited = key.split('.') // We need to check the name of deeply nested property, e.g opportunity.nid => nid
    const lastPieceOfKey = keySplited[keySplited.length - 1]
    const finalValue =
      trackingAllowedFields.includes(lastPieceOfKey) ||
      explicitlyAllowedFields?.includes(lastPieceOfKey)
        ? value
        : 'VALUE-HIDDEN'
    return { ...acc, [key]: finalValue }
  }, {})
}

const filterOutEmptyFields = (payload: object) => {
  return Object.entries(payload)
    .filter(([key, value]) => key && value !== undefined)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
}

export const transformToWebTrackingFormat = (
  payload: object
): WTPayload['values'] => {
  return Object.entries(payload).reduce(
    (acc: WTValue[], [key, value]) => [...acc, { type: key, value }],
    []
  )
}

export const transformThePayloadForTracking = (
  payload: unknown,
  config?: { explicitlyAllowedFields: string[] }
) => {
  return transformToWebTrackingFormat(
    joinArrayOfPrimitivesToString(
      filterOutEmptyFields(
        hideDisallowedToTrackData(
          flattenThePayload(payload),
          config?.explicitlyAllowedFields
        )
      )
    )
  )
}

export const triggerSearchResultsTracking = (
  searchParams: object,
  entityType: 'houses' | 'vendors',
  totalCount: number,
  location: string
) => {
  const wtValues = transformToWebTrackingFormat(searchParams)
  const pageParamNotExists = !wtValues.some(param => param.type === 'page')
  if (pageParamNotExists) {
    wtValues.push({ type: 'page', value: '1' })
  }
  wtValues.push({ type: 'count', value: String(totalCount) })

  window._wt({
    category: 'search-results',
    action: 'display',
    label: entityType === 'houses' ? 'house' : 'vendor',
    location,
    values: wtValues
  })
}
